import React, { PropsWithChildren } from 'react';
import {
  Cell,
  CellProps,
  Column,
  ColumnInstance,
  HeaderProps,
  Row,
  TableOptions,
  useFlexLayout,
  useGlobalFilter,
  useSortBy,
  useTable,
} from 'react-table';

import { User, UserRow, UserStatus } from '../types';
import { USER_ROLE } from '../../auth/systemRole';
import { DivButton } from '../../commons/components/DivButton';
import { BinIcon, EnvelopeIcon, PencilIcon, PlusSmallIcon } from '../../assets/icons';
import { UserAvatar } from '../../commons/components/UserAvatar';
import { GrowegyMenu } from '../../commons/components/GrowegyMenu';
import { useIsTabletOrLarger } from '../../responsive/hooks/hooks';
import { extractUserName } from '../utils';

interface TableProperties extends TableOptions<UserRow> {
  name: string;
  onUpdate: (user: User) => void;
  onDelete: (user: User) => void;
  onCreate: (user: User) => void;
  onRefresh: (userId: string) => Promise<unknown>;
  hasAddPermission: boolean;
  hasDeletePermission: boolean;
  hasRefreshPermission: boolean;
  hasReplacePermission: boolean;
}

const hooks = [useFlexLayout, useGlobalFilter, useSortBy];

const getSortCss = (column: ColumnInstance<UserRow>) => {
  if (column.isSorted && column.isSortedDesc) return 'users-table__sort--desc';
  if (column.isSorted && !column.isSortedDesc) return 'users-table__sort--asc';
  return '';
};

const NameCell = ({ value, row }: CellProps<UserRow, string>) => (
  <div className="users-table__name-cell" data-test="users-table__name-cell">
    <UserAvatar user={row.original} />
    <span className="text-capitalize text-break fs-mask" data-test="users-table__name">
      {value}
    </span>
  </div>
);

const NameHeader = ({ column }: HeaderProps<UserRow>) => (
  <div
    {...column.getSortByToggleProps()}
    data-test="users-table__name-header"
    className="users-table__header"
  >
    <span className="text-capitalize users-table__header-title">Name</span>
    <div className={`users-table__sort ${getSortCss(column)}`} />
  </div>
);

const EmailCell = ({ value }: CellProps<UserRow, string>) => (
  <div data-test="users-table__email-cell">
    <span className="text-break fs-mask">{value}</span>
  </div>
);

const EmailHeader = ({ column }: HeaderProps<UserRow>) => (
  <div
    {...column.getSortByToggleProps()}
    data-test="users-table__email-header"
    className="users-table__header"
  >
    <span className="text-capitalize users-table__header-title">Email</span>
    <div className={`users-table__sort ${getSortCss(column)}`} />
  </div>
);

const UserRoleCell = ({ value }: CellProps<UserRow, string>) => (
  <div data-test="users-table__role-cell">
    <span className="text-capitalize">{value}</span>
  </div>
);

const RoleHeader = () => (
  <div data-test="users-table__role-header">
    <span className="text-capitalize users-table__header-title">Role</span>
  </div>
);

type ActionCellProps = {
  onUpdate: (user: User) => void;
  onDelete: (user: User) => void;
  onRefresh: (userId: string) => Promise<unknown>;
  hasDeletePermission: boolean;
  hasRefreshPermission: boolean;
  hasReplacePermission: boolean;
} & CellProps<UserRow, UserStatus>;

const UserMenu = (props: {
  user: User;
  onUpdate: (user: User) => void;
  onDelete: (user: User) => void;
  onRefresh: (id: string) => void;
  hasDeletePermission: boolean;
  hasRefreshPermission: boolean;
  hasReplacePermission: boolean;
}) => {
  const {
    user,
    onUpdate,
    onDelete,
    onRefresh,
    hasDeletePermission,
    hasRefreshPermission,
    hasReplacePermission,
  } = props;
  return (
    <GrowegyMenu
      positionStrategy="fixed"
      menuItems={[
        {
          type: 'standard',
          text: 'Edit',
          icon: <PencilIcon />,
          onClick: () => onUpdate({ ...user }),
          isHidden: !hasReplacePermission,
          dataTest: 'users-table__edit-action',
        },
        {
          type: 'standard',
          text: 'Delete',
          icon: <BinIcon />,
          onClick: () => onDelete({ ...user }),
          isHidden: !hasDeletePermission,
          dataTest: 'users-table__delete-action',
        },
        {
          type: 'standard',
          text: 'Resend invite',
          icon: <EnvelopeIcon />,
          onClick: () => onRefresh(user.id),
          isHidden: !hasRefreshPermission || user.status !== 'need-to-change-password',
          dataTest: 'users-table__resend-invite-action',
        },
      ]}
      menuButtonDataTest="users-table__btn-action"
    />
  );
};

const ActionCell = ({
  row,
  onUpdate,
  onDelete,
  onRefresh,
  hasDeletePermission,
  hasRefreshPermission,
  hasReplacePermission,
}: ActionCellProps) => {
  const { original } = row;
  return (
    <div style={{ width: 'fit-content' }} data-test="users-table__actions-cell">
      <UserMenu
        user={original}
        onUpdate={onUpdate}
        onDelete={onDelete}
        onRefresh={onRefresh}
        hasDeletePermission={hasDeletePermission}
        hasRefreshPermission={hasRefreshPermission}
        hasReplacePermission={hasReplacePermission}
      />
    </div>
  );
};

const ActionHeader = () => <div />;

const tableColumns: Column<UserRow>[] = [
  {
    Header: NameHeader,
    id: 'username',
    accessor: (u: UserRow) => u.username,
    maxWidth: 280,
    Cell: NameCell,
  },
  {
    Header: EmailHeader,
    id: 'email',
    accessor: (u: UserRow) => u.email,
    maxWidth: 280,
    Cell: EmailCell,
  },
  {
    Header: RoleHeader,
    id: 'role',
    accessor: (u: UserRow) => u.role.name,
    maxWidth: 140,
    Cell: UserRoleCell,
  },
  {
    Header: ActionHeader,
    id: 'status',
    accessor: (u: UserRow) => u.status,
    // @ts-ignore
    Cell: ActionCell,
    disableGlobalFilter: true,
  },
];

const TableRow = (props: {
  row: Row<UserRow>;
  onUpdate: (user: User) => void;
  onDelete: (user: User) => void;
  onRefresh: (userId: string) => Promise<unknown>;
  hasDeleteAccess: boolean;
  hasRefreshAccess: boolean;
  hasReplaceAccess: boolean;
}) => {
  const {
    row,
    onUpdate,
    onDelete,
    onRefresh,
    hasDeleteAccess,
    hasRefreshAccess,
    hasReplaceAccess,
  } = props;

  return (
    <div {...row.getRowProps()} className="users-table__row" data-test="users-table__row">
      {row.cells.map((cell: Cell<UserRow>) => (
        <div {...cell.getCellProps()} key={cell.getCellProps().key}>
          {cell.render('Cell', {
            onUpdate,
            onDelete,
            onRefresh,
            key: cell.getCellProps().key,
            hasDeleteAccess,
            hasRefreshAccess,
            hasReplaceAccess,
          })}
        </div>
      ))}
    </div>
  );
};

const createNewUser = (): User => ({
  id: '',
  role: USER_ROLE,
  email: '',
  status: 'unknown',
  firstName: '',
  lastName: '',
  avatar: null,
});

const AddNewUserButton = (props: { hasAddPermission: boolean; onCreate: (user: User) => void }) => {
  const { onCreate, hasAddPermission } = props;

  return hasAddPermission ? (
    <DivButton
      icon={<PlusSmallIcon />}
      onClick={() => onCreate(createNewUser())}
      text="Add new"
      dataTest="user-edit__add-new"
      className="users-table__add-new-btn"
    />
  ) : null;
};

const Table = (props: PropsWithChildren<TableProperties>) => {
  const {
    columns,
    onDelete,
    onUpdate,
    onRefresh,
    onCreate,
    hasAddPermission,
    hasDeletePermission,
    hasRefreshPermission,
    hasReplacePermission,
  } = props;

  const instance = useTable<UserRow>(
    {
      ...props,
      columns,
      autoResetGlobalFilter: true,
      autoResetSortBy: false,
    },
    ...hooks
  );

  const { getTableProps, getTableBodyProps, rows, prepareRow, headerGroups } = instance;

  return (
    <>
      <div {...getTableProps()} className="settings__table-content">
        <div>
          {headerGroups.map((headerGroup) => (
            <div
              {...headerGroup.getHeaderGroupProps()}
              key={headerGroup.getHeaderGroupProps().key}
              className="users-table__row users-table__row--header"
            >
              {headerGroup.headers.map((column) => (
                <div {...column.getHeaderProps()} key={column.getHeaderProps().key}>
                  {column.render('Header', { key: column.getHeaderProps().key })}
                </div>
              ))}
            </div>
          ))}
        </div>
        <div {...getTableBodyProps()} className="settings__table-body">
          {rows.map((row: Row<UserRow>) => {
            prepareRow(row);
            return (
              <TableRow
                key={row.getRowProps().key}
                onRefresh={onRefresh}
                onDelete={onDelete}
                onUpdate={onUpdate}
                row={row}
                hasDeleteAccess={hasDeletePermission}
                hasRefreshAccess={hasRefreshPermission}
                hasReplaceAccess={hasReplacePermission}
              />
            );
          })}
        </div>
      </div>
      <AddNewUserButton hasAddPermission={hasAddPermission} onCreate={onCreate} />
    </>
  );
};

const ShortUserRow = (props: {
  user: UserRow;
  onUpdate: (user: User) => void;
  onDelete: (user: User) => void;
  onRefresh: (userId: string) => Promise<unknown>;
  hasAddPermission: boolean;
  hasDeletePermission: boolean;
  hasRefreshPermission: boolean;
  hasReplacePermission: boolean;
}) => {
  const { user, onUpdate } = props;
  return (
    <div className="users-table__item-container" onClick={() => onUpdate(user)}>
      <div className="users-table__item-row fs-mask">
        <UserAvatar user={user} />
        <span>{extractUserName(user)}</span>
        <span className="app-sidebar__account-role">{user.role.name}</span>
      </div>
      <div className="users-table__item-row">
        <span className="text-break fs-mask flex-grow-1">{user.email}</span>
        <UserMenu {...props} />
      </div>
    </div>
  );
};

export const UsersTable = (props: {
  data: UserRow[];
  onUpdate: (user: User) => void;
  onDelete: (user: User) => void;
  onCreate: (user: User) => void;
  onRefresh: (userId: string) => Promise<unknown>;
  hasAddPermission: boolean;
  hasDeletePermission: boolean;
  hasRefreshPermission: boolean;
  hasReplacePermission: boolean;
}) => {
  const isTabletOrLarger = useIsTabletOrLarger();
  return isTabletOrLarger ? (
    <Table
      key="users-table"
      name="users-table"
      columns={tableColumns}
      data={props.data}
      onUpdate={props.onUpdate}
      onDelete={props.onDelete}
      onCreate={props.onCreate}
      onRefresh={props.onRefresh}
      hasAddPermission={props.hasAddPermission}
      hasDeletePermission={props.hasDeletePermission}
      hasRefreshPermission={props.hasRefreshPermission}
      hasReplacePermission={props.hasReplacePermission}
    />
  ) : (
    <div className="users-table__items-list">
      {props.data.map((user) => (
        <ShortUserRow
          key={user.id}
          user={user}
          onUpdate={props.onUpdate}
          onDelete={props.onDelete}
          onRefresh={props.onRefresh}
          hasAddPermission={props.hasAddPermission}
          hasDeletePermission={props.hasDeletePermission}
          hasRefreshPermission={props.hasRefreshPermission}
          hasReplacePermission={props.hasReplacePermission}
        />
      ))}
      <AddNewUserButton hasAddPermission={props.hasAddPermission} onCreate={props.onCreate} />
    </div>
  );
};
